// HolidaysAdmin — gesetzliche Feiertage pro Tenant + Jahr (Einstellungen-Tab).
// Admin / Geschäftsführung: anlegen, bearbeiten, löschen + "Standard-Feiertage
// Österreich" seed pro Jahr (13 Einträge).
import { useEffect, useMemo, useState } from "react";
import { Calendar, Plus, Edit3, Trash2, Wand2, X, Save, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useBulkSelect, BulkSelectAll, BulkSelectCheckbox, BulkDeleteBar } from "@/components/BulkDeleteBar";

const fmtDate = (s) => {
  if (!s || s.length < 10) return s;
  return new Date(s + "T00:00:00").toLocaleDateString("de-AT", { weekday: "short", day: "2-digit", month: "long", year: "numeric" });
};

export default function HolidaysAdmin() {
  const [rows, setRows] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);  // {id?, date, name, hours_paid}
  const sel = useBulkSelect(rows);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(`/settings/holidays?year=${year}`);
      setRows(data.holidays || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Konnte Feiertage nicht laden");
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year]);

  const yearOpts = useMemo(() => {
    const cur = new Date().getFullYear();
    return Array.from({ length: 6 }, (_, i) => cur - 1 + i);
  }, []);

  async function seedAustria() {
    if (!window.confirm(`Standard-Feiertage Österreich für ${year} einfügen (13 Einträge, ohne Karfreitag)?`)) return;
    try {
      const { data } = await api.post("/settings/holidays/seed-austria", { year });
      toast.success(`${data.inserted} eingefügt · ${data.skipped_existing} bereits vorhanden`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Seed fehlgeschlagen");
    }
  }

  async function save() {
    const body = {
      date: editing.date,
      name: (editing.name || "").trim(),
      hours_paid: parseFloat(editing.hours_paid || 8),
    };
    try {
      if (editing.id) {
        await api.put(`/settings/holidays/${editing.id}`, body);
        toast.success("Aktualisiert");
      } else {
        await api.post("/settings/holidays", body);
        toast.success("Hinzugefügt");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    }
  }

  async function remove(h) {
    if (!window.confirm(`„${h.name}" (${h.date}) wirklich löschen?`)) return;
    try {
      await api.delete(`/settings/holidays/${h.id}`);
      toast.success("Gelöscht");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  }

  const INP = "px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 focus:border-rose-500/60 outline-none";

  return (
    <div className="space-y-5" data-testid="holidays-admin">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display text-zinc-50 font-semibold flex items-center gap-2">
            <Calendar className="w-4 h-4 text-rose-400" /> Feiertage Österreich
          </h3>
          <p className="text-xs text-zinc-500 mt-0.5 max-w-2xl">
            Pflegt die gesetzlichen Feiertage pro Jahr. Auto-Generator überspringt diese Tage automatisch; Lohnabrechnung addiert pro Feiertag 8h Feiertagsentgelt (ohne Diät) wenn nicht gearbeitet wurde.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={year} onChange={e => setYear(parseInt(e.target.value, 10))}
            data-testid="holidays-year-select" className={INP}>
            {yearOpts.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={seedAustria} data-testid="holidays-seed-at"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium bg-amber-600 hover:bg-amber-700 text-white rounded">
            <Wand2 className="w-3.5 h-3.5" /> Standard {year} einfügen
          </button>
          <button onClick={() => setEditing({ date: `${year}-01-01`, name: "", hours_paid: 8 })}
            data-testid="holidays-add"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium bg-blue-600 hover:bg-blue-700 text-white rounded">
            <Plus className="w-3.5 h-3.5" /> Hinzufügen
          </button>
        </div>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-md overflow-hidden">
        {loading && (
          <div className="px-5 py-3 text-xs text-zinc-500 inline-flex items-center gap-2">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Lädt…
          </div>
        )}
        {!loading && rows.length === 0 && (
          <div className="px-5 py-8 text-center text-sm text-zinc-500">
            Keine Feiertage für {year}. Nutze „Standard {year} einfügen" für die 13 österreichischen Feiertage.
          </div>
        )}
        {rows.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-zinc-900/60 text-[10px] uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="px-3 py-2 w-8"><BulkSelectAll sel={sel} testid="holiday-select-all" /></th>
                <th className="px-4 py-2 text-left">Datum</th>
                <th className="px-4 py-2 text-left">Name</th>
                <th className="px-4 py-2 text-right">Stunden</th>
                <th className="px-4 py-2 w-20"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {rows.map(h => (
                <tr key={h.id} data-testid={`holiday-row-${h.id}`} className="hover:bg-zinc-800/40">
                  <td className="px-3 py-2.5 text-center">
                    <BulkSelectCheckbox sel={sel} id={h.id} testid={`holiday-select-${h.id}`} />
                  </td>
                  <td className="px-4 py-2 text-zinc-200">
                    <div className="font-mono text-xs text-zinc-400">{(() => { try { const d = new Date(h.date + "T00:00:00"); return `${String(d.getDate()).padStart(2,"0")}.${String(d.getMonth()+1).padStart(2,"0")}.${d.getFullYear()}`; } catch { return h.date; } })()}</div>
                    <div className="text-[11px] text-zinc-500">{fmtDate(h.date)}</div>
                  </td>
                  <td className="px-4 py-2 text-zinc-100">{h.name}</td>
                  <td className="px-4 py-2 text-right font-mono text-xs text-zinc-200">{h.hours_paid}h</td>
                  <td className="px-4 py-2">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => setEditing(h)} data-testid={`holiday-edit-${h.id}`}
                        className="p-1.5 text-zinc-400 hover:text-blue-400 hover:bg-blue-500/10 rounded">
                        <Edit3 className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => remove(h)} data-testid={`holiday-delete-${h.id}`}
                        className="p-1.5 text-zinc-400 hover:text-red-400 hover:bg-red-500/10 rounded">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <BulkDeleteBar
        sel={sel}
        label="Feiertage"
        deleteEndpoint={(id) => `/settings/holidays/${id}`}
        onAfter={load}
        testid="holiday-bulk-delete"
      />

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg w-full max-w-md">
            <header className="px-5 py-3 border-b border-zinc-800 flex items-center justify-between">
              <h3 className="text-zinc-100 font-display font-semibold flex items-center gap-2">
                <Calendar className="w-4 h-4 text-rose-400" />
                {editing.id ? "Feiertag bearbeiten" : "Feiertag hinzufügen"}
              </h3>
              <button onClick={() => setEditing(null)} className="text-zinc-500 hover:text-zinc-200">
                <X className="w-4 h-4" />
              </button>
            </header>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Datum</label>
                <input type="date" className={INP + " w-full"}
                  value={editing.date}
                  data-testid="holiday-form-date"
                  onChange={e => setEditing(p => ({ ...p, date: e.target.value }))}/>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Name</label>
                <input type="text" className={INP + " w-full"}
                  value={editing.name}
                  data-testid="holiday-form-name"
                  placeholder="z.B. Nationalfeiertag"
                  onChange={e => setEditing(p => ({ ...p, name: e.target.value }))}/>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Bezahlte Stunden (für Lohnabrechnung)</label>
                <input type="number" step="0.25" min="0" max="24" className={INP + " w-full"}
                  value={editing.hours_paid}
                  data-testid="holiday-form-hours"
                  onChange={e => setEditing(p => ({ ...p, hours_paid: e.target.value }))}/>
              </div>
            </div>
            <footer className="px-5 py-3 border-t border-zinc-800 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm text-zinc-400 hover:text-zinc-200">
                Abbrechen
              </button>
              <button onClick={save} data-testid="holiday-form-save"
                disabled={!editing.date || !editing.name?.trim()}
                className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-30 text-white rounded font-medium inline-flex items-center gap-1.5">
                <Save className="w-3.5 h-3.5" /> Speichern
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}
